import { CartLinesUpdateEvent } from '@shopify/events';

const STORAGE_KEY = 'taj-heirmuffs-builder-v1';
const STONES = ['D-Color VVS Moissanite', 'Lab-Grown Diamond', 'Natural Diamond'];
const COLLECTION_LABELS = {
  'interchangeable-stud-earrings': 'THE FOUNDATION',
  'hoop-and-huggie-earrings': 'FULL CIRCLE',
  'drop-and-dangle-earrings': 'IN MOTION',
  'birthstone-earrings-by-month': 'BORN BRILLIANT',
  'childrens-first-earrings': 'LITTLE HEIRS',
  'heart-and-love-earrings': 'LOVE LANGUAGE',
  'city-initial-earrings': "WHERE I'M FROM",
  'faith-and-religious-earrings': 'COVERED',
  'sports-earrings': 'THE GAME',
  'zodiac-sign-earrings': 'WRITTEN IN THE STARS'
};

const escapeHTML = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;'
}[char]));
const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
  style: 'currency', currency: window.Shopify?.currency?.active || 'USD'
}).format((Number(cents) || 0) / 100);
const debounce = (callback, wait = 220) => {
  let timer;
  return (...args) => { clearTimeout(timer); timer = setTimeout(() => callback(...args), wait); };
};
const productImage = (product, variant) => variant?.featured_image?.src || variant?.featured_image || product?.featured_image || product?.images?.[0]?.src || product?.images?.[0] || '';

// Collection product JSON uses decimal currency values; product .js uses integer cents.
const catalogPriceToCents = (value) => Math.round((Number(value) || 0) * 100);
const availableCatalogPrices = (product) => (product.variants || [])
  .filter((variant) => variant.available)
  .map((variant) => catalogPriceToCents(variant.price))
  .filter((price) => Number.isFinite(price) && price > 0);
const minimumAvailableCatalogPrice = (product) => {
  const prices = availableCatalogPrices(product);
  return prices.length ? Math.min(...prices) : null;
};

const normalizeMetal = (values) => {
  const text = (Array.isArray(values) ? values : [values]).filter(Boolean).join(' | ').toLowerCase().replace(/\s+/g, ' ').trim();
  const matches = [];
  if (text.includes('gold vermeil')) matches.push('gold-vermeil');
  else if (text.includes('.925 sterling silver')) matches.push('sterling-silver');
  for (const karat of ['10k', '14k']) {
    for (const color of ['yellow', 'white', 'rose']) {
      if (text.includes(karat + ' ' + color + ' gold') || text.includes(karat + ' gold / ' + color + ' gold')) {
        matches.push(karat + '-' + color + '-gold');
      }
    }
  }
  return matches.length === 1 ? matches[0] : '';
};

class HeirmuffsSystemBuilder extends HTMLElement {
  constructor() {
    super();
    this.state = { path: 'first', step: 0, front: null, returningFronts: [], stemType: '', stem: null, backs: null };
    this.products = [];
    this.filtered = [];
    this.details = new Map();
    this.visibleCount = 24;
    this.busy = false;
  }

  connectedCallback() {
    this.form = this.querySelector('[data-builder-form]');
    this.status = this.querySelector('[data-status]');
    this.errors = this.querySelector('[data-errors]');
    this.nextButton = this.querySelector('[data-next]');
    this.backButton = this.querySelector('[data-back]');
    this.results = this.querySelector('[data-front-results]');
    this.resultsCount = this.querySelector('[data-results-count]');
    this.frontSelection = this.querySelector('[data-front-selection]');
    this.stemSelection = this.querySelector('[data-stem-selection]');
    this.backsSelection = this.querySelector('[data-backs-selection]');
    this.review = this.querySelector('[data-review]');
    this.returningList = this.querySelector('[data-returning-list]');
    this.taxonomy = this.readTaxonomy();
    this.restore();
    this.bind();
    this.loadCatalog();
  }

  readTaxonomy() {
    try { return JSON.parse(this.querySelector('[data-builder-taxonomy]')?.textContent || '{}'); }
    catch (error) { return {}; }
  }

  bind() {
    this.form.addEventListener('change', (event) => this.onChange(event));
    this.form.addEventListener('click', (event) => this.onClick(event));
    this.nextButton.addEventListener('click', () => this.move(1));
    this.backButton.addEventListener('click', () => this.move(-1));
    this.querySelector('[data-filter-search]').addEventListener('input', debounce(() => this.applyFilters()));
    ['[data-filter-collection]', '[data-filter-stone]', '[data-filter-price]', '[data-filter-available]']
      .forEach((selector) => this.querySelector(selector).addEventListener('change', () => this.applyFilters()));
    this.querySelectorAll('input[name="customer_path"]').forEach((input) => {
      input.checked = input.value === this.state.path;
    });
  }

  async loadCatalog() {
    this.results.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(this.dataset.catalogUrl, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      if (!response.ok) throw new Error('The Front Set catalog could not be loaded.');
      const payload = await response.json();
      this.products = (payload.products || []).filter((product) =>
        product.product_type === 'HEIRMUFFS Front Set' &&
        !['heirmuffs-adult-universal-stem-pair', 'heirmuffs-childrens-universal-stem-pair', 'heirmuffs-universal-screw-back-pair'].includes(product.handle)
      );
      this.populateCollectionFilter();
      const requestedStone = new URL(window.location.href).searchParams.get('stone');
      if (STONES.includes(requestedStone)) this.querySelector('[data-filter-stone]').value = requestedStone;
      this.applyFilters();
      await this.hydrateSavedSelections();
      this.showStep(this.state.step, false);
    } catch (error) {
      this.showError(error.message + ' Please refresh the page or explore Front Sets directly.');
      this.results.innerHTML = '<p><a href="' + escapeHTML(this.dataset.foundationUrl) + '">Explore Front Sets</a></p>';
      this.resultsCount.textContent = 'Front Sets are temporarily unavailable in the builder.';
    } finally {
      this.results.setAttribute('aria-busy', 'false');
    }
  }

  populateCollectionFilter() {
    const select = this.querySelector('[data-filter-collection]');
    Object.entries(COLLECTION_LABELS).forEach(([handle, label]) => {
      if ((this.taxonomy[handle] || []).some((productHandle) => this.products.some((product) => product.handle === productHandle))) {
        const option = document.createElement('option');
        option.value = handle;
        option.textContent = label;
        select.appendChild(option);
      }
    });
  }

  productStoneValues(product) {
    const values = new Set();
    (product.variants || []).forEach((variant) => {
      [variant.option1, variant.option2, variant.option3].forEach((value) => {
        STONES.forEach((stone) => {
          if (String(value || '').includes(stone)) values.add(stone);
        });
      });
    });
    return [...values];
  }

  productAvailable(product) {
    return (product.variants || []).some((variant) => variant.available);
  }

  applyFilters(resetCount = true) {
    if (resetCount) this.visibleCount = 24;
    const term = this.querySelector('[data-filter-search]').value.trim().toLowerCase();
    const collection = this.querySelector('[data-filter-collection]').value;
    const stone = this.querySelector('[data-filter-stone]').value;
    const price = this.querySelector('[data-filter-price]').value;
    const availableOnly = this.querySelector('[data-filter-available]').checked;
    this.filtered = this.products.filter((product) => {
      if (term && !product.title.toLowerCase().includes(term)) return false;
      if (collection && !(this.taxonomy[collection] || []).includes(product.handle)) return false;
      if (stone && !this.productStoneValues(product).includes(stone)) return false;
      if (availableOnly && !this.productAvailable(product)) return false;
      const availablePrices = availableCatalogPrices(product);
      if (price === 'under-100' && !availablePrices.some((amount) => amount < 10000)) return false;
      if (price === '100-500' && !availablePrices.some((amount) => amount >= 10000 && amount <= 50000)) return false;
      if (price === '500-plus' && !availablePrices.some((amount) => amount > 50000)) return false;
      return true;
    });
    this.renderResults();
  }

  renderResults() {
    const visible = this.filtered.slice(0, this.visibleCount);
    this.resultsCount.textContent = this.filtered.length
      ? this.filtered.length + ' Front Set' + (this.filtered.length === 1 ? '' : 's') + ' found.'
      : 'No Front Sets match those selections.';
    if (!visible.length) {
      this.results.innerHTML = '<div class="taj-empty"><p>Try clearing a filter or choosing another stone.</p><button type="button" class="button button-secondary" data-empty-clear>Clear filters</button></div>';
      return;
    }
    const selectedHandle = this.state.front?.handle;
    this.results.innerHTML = visible.map((product) => {
      const image = product.images?.[0]?.src || product.images?.[0] || '';
      const minimum = minimumAvailableCatalogPrice(product);
      return '<button type="button" class="taj-product-choice" data-front-handle="' + escapeHTML(product.handle) + '" aria-pressed="' + (selectedHandle === product.handle) + '"' + (this.productAvailable(product) ? '' : ' disabled') + '>' +
        (image ? '<img src="' + escapeHTML(image) + '" alt="" loading="lazy" width="480" height="480">' : '<span aria-hidden="true"></span>') +
        '<span><strong>' + escapeHTML(product.title) + '</strong>' + (minimum == null ? '' : '<small>From ' + money(minimum) + '</small>') + '<small>' + (this.productAvailable(product) ? 'Choose options' : 'Currently unavailable') + '</small></span></button>';
    }).join('') + (this.filtered.length > this.visibleCount
      ? '<button type="button" class="button button-secondary taj-load-more" data-load-more>Show more Front Sets</button>' : '');
  }

  async onClick(event) {
    const target = event.target.closest('button');
    if (!target) return;
    if (target.matches('[data-front-handle]')) await this.selectFront(target.dataset.frontHandle);
    if (target.matches('[data-load-more]')) { this.visibleCount += 24; this.renderResults(); }
    if (target.matches('[data-clear-filters], [data-empty-clear]')) this.clearFilters();
    if (target.matches('[data-add-returning]')) this.addReturningFront();
    if (target.matches('[data-remove-returning]')) this.removeReturningFront(Number(target.dataset.removeReturning));
    if (target.matches('[data-add-cart]')) await this.addToCart();
  }

  async onChange(event) {
    const target = event.target;
    target.removeAttribute?.('aria-invalid');
    if (target.getAttribute?.('aria-describedby') === 'taj-builder-errors') target.removeAttribute('aria-describedby');
    if (target.name === 'customer_path') {
      this.state.path = target.value;
      if (target.value === 'returning' && this.state.step > 1) this.state.step = 1;
      if (this.state.front?.product) {
        this.renderProductSelection('front', this.state.front.product, this.frontSelection, this.state.front.variant);
        this.frontSelection.hidden = false;
      }
      this.renderReturningList();
      this.updateProgressModel();
      this.save();
      this.announce(target.value === 'first' ? 'First-time system pathway selected. Five steps are available.' : 'Returning-customer pathway selected. Three steps are available.');
      return;
    }
    if (target.name === 'stem_type') {
      this.state.stemType = target.value;
      this.state.stem = null;
      await this.loadStem();
      this.save();
      return;
    }
    const role = target.closest('[data-product-role]')?.dataset.productRole;
    if (target.matches('[data-option-select]') && role) this.resolveVariant(role);
  }

  clearFilters() {
    this.querySelector('[data-filter-search]').value = '';
    this.querySelector('[data-filter-collection]').value = '';
    this.querySelector('[data-filter-stone]').value = '';
    this.querySelector('[data-filter-price]').value = '';
    this.querySelector('[data-filter-available]').checked = false;
    history.replaceState(null, '', window.location.pathname + '#build-your-system');
    this.applyFilters();
    this.querySelector('[data-filter-search]').focus();
  }

  async fetchProduct(url) {
    if (this.details.has(url)) return this.details.get(url);
    const response = await fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    if (!response.ok) throw new Error('Product details could not be loaded.');
    const product = await response.json();
    this.details.set(url, product);
    return product;
  }

  async selectFront(handle) {
    this.clearError();
    const card = this.results.querySelector('[data-front-handle="' + CSS.escape(handle) + '"]');
    card?.setAttribute('aria-busy', 'true');
    try {
      const product = await this.fetchProduct('/products/' + encodeURIComponent(handle) + '.js');
      this.state.front = { handle, product, variant: null, options: [] };
      this.renderResults();
      this.renderProductSelection('front', product, this.frontSelection);
      this.frontSelection.hidden = false;
      this.frontSelection.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'nearest' });
      this.save();
    } catch (error) { this.showError(error.message); }
    finally { card?.removeAttribute('aria-busy'); }
  }

  renderProductSelection(role, product, container, savedVariant = null) {
    const image = productImage(product, savedVariant);
    const optionMarkup = (product.options || []).map((option, index) => {
      const values = option.values || [...new Set(product.variants.map((variant) => variant.options[index]))];
      return '<label>' + escapeHTML(option.name) + '<select data-option-select data-option-index="' + index + '" required><option value="">Choose ' + escapeHTML(option.name) + '</option>' +
        values.map((value) => '<option value="' + escapeHTML(value) + '"' + (savedVariant?.options?.[index] === value ? ' selected' : '') + '>' + escapeHTML(value) + '</option>').join('') +
        '</select><span class="sr-only" data-option-error></span></label>';
    }).join('');
    container.innerHTML = '<article class="taj-selected-product" data-product-role="' + role + '">' +
      (image ? '<img src="' + escapeHTML(image) + '" alt="' + escapeHTML(product.title) + '" loading="lazy" width="520" height="520" data-selected-image>' : '') +
      '<div><p class="taj-kicker">' + (role === 'front' ? 'Selected Front Set' : role === 'stem' ? 'Selected Stem Set' : 'Universal Screw-Backs') + '</p>' +
      '<h4>' + escapeHTML(product.title) + '</h4><p class="taj-selected-product__price" data-selected-price>Choose options to see price</p>' +
      '<div class="taj-variant-fields">' + optionMarkup + '</div><p class="taj-availability" data-availability>Choose every option.</p>' +
      (role === 'front' && this.state.path === 'returning' ? '<button type="button" class="button button-secondary" data-add-returning disabled>Add this Front Set to review</button>' : '') +
      '</div></article>';
    if (savedVariant) this.resolveVariant(role, savedVariant.id);
  }

  getRoleState(role) {
    return role === 'front' ? this.state.front : role === 'stem' ? this.state.stem : this.state.backs;
  }

  resolveVariant(role, forcedId) {
    const state = this.getRoleState(role);
    if (!state?.product) return;
    const container = this.querySelector('[data-product-role="' + role + '"]');
    const selects = [...container.querySelectorAll('[data-option-select]')];
    const selected = selects.map((select) => select.value);
    const variant = forcedId
      ? state.product.variants.find((item) => Number(item.id) === Number(forcedId))
      : selected.every(Boolean) ? state.product.variants.find((item) => item.options.every((value, index) => value === selected[index])) : null;
    state.options = selected;
    state.variant = variant || null;

    selects.forEach((select, index) => {
      [...select.options].forEach((option) => {
        if (!option.value) return;
        const possible = state.product.variants.some((item) =>
          item.available && item.options[index] === option.value &&
          selected.every((value, selectedIndex) => !value || selectedIndex === index || item.options[selectedIndex] === value)
        );
        option.disabled = !possible;
      });
    });

    const availability = container.querySelector('[data-availability]');
    const price = container.querySelector('[data-selected-price]');
    const image = container.querySelector('[data-selected-image]');
    if (!variant) {
      availability.textContent = selected.every(Boolean) ? 'That option combination is unavailable.' : 'Choose every option.';
      availability.dataset.state = 'unavailable';
      price.textContent = 'Choose options to see price';
    } else if (!variant.available) {
      availability.textContent = 'This selected variant is currently unavailable.';
      availability.dataset.state = 'unavailable';
      price.textContent = money(variant.price);
    } else {
      availability.textContent = 'Available';
      availability.dataset.state = 'available';
      price.textContent = money(variant.price);
      const nextImage = productImage(state.product, variant);
      if (image && nextImage) image.src = nextImage;
    }
    const returningButton = container.querySelector('[data-add-returning]');
    if (returningButton) returningButton.disabled = !variant?.available;
    this.save();
  }

  addReturningFront() {
    if (!this.state.front?.variant?.available) return;
    if (!this.state.returningFronts.some((item) => Number(item.variant.id) === Number(this.state.front.variant.id))) {
      this.state.returningFronts.push(this.state.front);
    }
    this.renderReturningList();
    this.save();
    this.announce(this.state.front.product.title + ' added to your builder review.');
  }

  removeReturningFront(index) {
    this.state.returningFronts.splice(index, 1);
    this.renderReturningList();
    this.save();
  }

  renderReturningList() {
    if (this.state.path !== 'returning' || !this.state.returningFronts.length) {
      this.returningList.hidden = true;
      return;
    }
    this.returningList.hidden = false;
    this.returningList.innerHTML = '<strong>Front Sets ready for review</strong><ul>' +
      this.state.returningFronts.map((item, index) => '<li><span>' + escapeHTML(item.product.title) + ' — ' + escapeHTML(item.variant.title) + '</span><button type="button" data-remove-returning="' + index + '" aria-label="Remove ' + escapeHTML(item.product.title) + ' from builder review">Remove</button></li>').join('') + '</ul>';
  }

  async loadStem(savedVariant) {
    if (!this.state.stemType) { this.stemSelection.innerHTML = ''; return; }
    try {
      const url = this.state.stemType === 'adult' ? this.dataset.adultUrl : this.dataset.childUrl;
      const product = await this.fetchProduct(url);
      this.state.stem = { handle: product.handle, product, variant: null, options: [] };
      this.renderProductSelection('stem', product, this.stemSelection, savedVariant);
    } catch (error) { this.showError(error.message); }
  }

  async loadBacks(savedVariant) {
    try {
      const product = await this.fetchProduct(this.dataset.backsUrl);
      this.state.backs = { handle: product.handle, product, variant: null, options: [] };
      this.renderProductSelection('backs', product, this.backsSelection, savedVariant);
    } catch (error) { this.showError(error.message); }
  }

  validateStep() {
    this.clearError();
    if (this.state.step === 1) {
      if (this.state.path === 'returning' && !this.state.returningFronts.length) return this.fail('Choose a Front Set variant and add it to your review.', this.querySelector('[data-add-returning]') || this.querySelector('[data-front-handle]'));
      if (this.state.path === 'first' && !this.state.front?.variant?.available) return this.fail('Choose an available Front Set and complete its options.', this.querySelector('[data-product-role="front"] [data-option-select]') || this.querySelector('[data-front-handle]'));
    }
    if (this.state.step === 2 && !this.state.stem?.variant?.available) return this.fail('Choose Adult or Child, then select an available Stem Set variant.', this.querySelector('[data-product-role="stem"] [data-option-select]') || this.querySelector('input[name="stem_type"]'));
    if (this.state.step === 3 && !this.state.backs?.variant?.available) return this.fail('Select an available Universal Screw-Back variant.', this.querySelector('[data-product-role="backs"] [data-option-select]'));
    return true;
  }

  async move(direction) {
    if (direction > 0 && !this.validateStep()) return;
    let next = this.state.step + direction;
    if (this.state.path === 'returning') {
      if (direction > 0 && this.state.step === 1) next = 4;
      if (direction < 0 && this.state.step === 4) next = 1;
    }
    next = Math.max(0, Math.min(4, next));
    if (next === 2 && this.state.stemType && !this.state.stem) await this.loadStem();
    if (next === 3 && !this.state.backs) await this.loadBacks();
    this.showStep(next);
  }

  progressModel() {
    return this.state.path === 'returning'
      ? [
          { stage: 0, number: 1, total: 3, label: 'Your Path' },
          { stage: 1, number: 2, total: 3, label: 'Front Sets' },
          { stage: 4, number: 3, total: 3, label: 'Review' }
        ]
      : [
          { stage: 0, number: 1, total: 5, label: 'Your Path' },
          { stage: 1, number: 2, total: 5, label: 'Front Set' },
          { stage: 2, number: 3, total: 5, label: 'Stem Set' },
          { stage: 3, number: 4, total: 5, label: 'Screw-Backs' },
          { stage: 4, number: 5, total: 5, label: 'Review' }
        ];
  }

  updateProgressModel() {
    const model = this.progressModel();
    const progressList = this.querySelector('.taj-builder__progress ol');
    progressList?.style.setProperty('--taj-progress-count', String(model.length));
    this.querySelectorAll('[data-progress]').forEach((item) => {
      const entry = model.find((candidate) => candidate.stage === Number(item.dataset.progress));
      item.hidden = !entry;
      if (!entry) return;
      item.querySelector('[data-progress-number]').textContent = entry.number;
      item.querySelector('[data-progress-label]').textContent = entry.label;
      if (entry.stage === this.state.step) item.setAttribute('aria-current', 'step'); else item.removeAttribute('aria-current');
      const currentIndex = model.findIndex((candidate) => candidate.stage === this.state.step);
      const itemIndex = model.findIndex((candidate) => candidate.stage === entry.stage);
      item.dataset.complete = String(itemIndex < currentIndex);
    });
    this.querySelectorAll('[data-step-kicker]').forEach((kicker) => {
      const entry = model.find((candidate) => candidate.stage === Number(kicker.closest('[data-step]').dataset.step));
      if (entry) kicker.textContent = 'Step ' + entry.number + ' of ' + entry.total;
    });
    const reviewHeading = this.querySelector('[data-review-heading]');
    if (reviewHeading) reviewHeading.textContent = this.state.path === 'returning' ? 'Review Your Front Sets' : 'Review Your HEIRMUFFS™ System';
  }

  showStep(step, focus = true) {
    this.state.step = step;
    this.querySelectorAll('[data-step]').forEach((section) => { section.hidden = Number(section.dataset.step) !== step; });
    this.updateProgressModel();
    this.backButton.hidden = step === 0;
    this.nextButton.hidden = step === 4;
    const labels = ['Choose a Front Set', this.state.path === 'returning' ? 'Review selected Front Sets' : 'Choose a Stem Set', 'Choose Universal Screw-Backs', 'Review your selections'];
    if (step < 4) this.nextButton.textContent = labels[step] || 'Continue';
    if (step === 1) this.renderReturningList();
    if (step === 4) this.renderReview();
    this.save();
    const heading = this.querySelector('[data-step="' + step + '"] h3, [data-step="' + step + '"] legend');
    if (focus) {
      heading?.focus();
      const entry = this.progressModel().find((candidate) => candidate.stage === step);
      this.announce('Step ' + entry.number + ' of ' + entry.total + '. ' + (heading?.textContent || ''));
    }
  }

  renderReview() {
    const items = this.getItems();
    this.review.innerHTML = '<ul class="taj-review-list">' + items.map((item) => {
      const image = productImage(item.product, item.variant);
      return '<li class="taj-review-item">' + (image ? '<img src="' + escapeHTML(image) + '" alt="" width="176" height="176">' : '') +
        '<div><h4>' + escapeHTML(item.product.title) + '</h4><p>' + escapeHTML(item.variant.title) + '</p><small>Quantity 1 · ' + (item.variant.available ? 'Available' : 'Unavailable') + '</small></div><strong>' + money(item.variant.price) + '</strong></li>';
    }).join('') + '</ul><p class="taj-review-total"><span>Combined subtotal</span><span>' + money(items.reduce((sum, item) => sum + Number(item.variant.price), 0)) + '</span></p>';
    const disclosure = this.querySelector('[data-disclosure]');
    disclosure.textContent = this.state.path === 'returning'
      ? 'Only your selected Front Set' + (items.length === 1 ? '' : 's') + ' will be added. You confirmed that you already own compatible HEIRMUFFS™ Stems and Universal Screw-Backs.'
      : 'Your selections are three separately sold products. Each item will appear as a separate line in your cart.';
    const notice = this.querySelector('[data-metal-notice]');
    notice.hidden = !this.hasMetalMismatch(items);
    const add = this.querySelector('[data-add-cart]');
    add.disabled = !items.length || items.some((item) => !item.variant.available);
    add.textContent = this.state.path === 'first' ? 'Add 3 Selected Products to Cart' : 'Add ' + items.length + ' Selected Front Set' + (items.length === 1 ? '' : 's') + ' to Cart';
  }

  getItems() {
    if (this.state.path === 'returning') return this.state.returningFronts.filter((item) => item?.variant);
    return [this.state.front, this.state.stem, this.state.backs].filter((item) => item?.variant);
  }

  metalFor(item) {
    if (!item?.variant) return '';
    return normalizeMetal(item.variant.options);
  }

  hasMetalMismatch(items) {
    const metals = items.map((item) => this.metalFor(item)).filter(Boolean);
    return new Set(metals).size > 1;
  }

  async addToCart() {
    if (this.busy) return;
    const items = this.getItems();
    if (!items.length || items.some((item) => !item.variant?.available)) return this.fail('Review each selection and choose available variants before adding to cart.');
    this.busy = true;
    const button = this.querySelector('[data-add-cart]');
    const original = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding selections…';
    this.clearError();
    this.announce('Adding your selected products to cart.');

    const cartComponents = [...document.querySelectorAll('cart-items-component')];
    const sectionIds = cartComponents.map((component) => component.dataset.sectionId).filter(Boolean);
    const eventPromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.variant.id), quantity: 1 })),
      promise: eventPromise.promise
    }));

    try {
      const response = await fetch(this.dataset.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          items: items.map((item) => ({ id: Number(item.variant.id), quantity: 1 })),
          sections: sectionIds.join(',')
        })
      });
      const payload = await response.json();
      if (!response.ok || payload.status) throw new Error(payload.description || payload.message || 'The selected products could not be added.');

      let cart;
      const component = cartComponents[0];
      if (component?.fetchCartData) {
        await customElements.whenDefined('cart-items-component');
        cart = await component.fetchCartData();
      } else {
        cart = await fetch(this.dataset.cartUrl + '.js', { credentials: 'same-origin' }).then((result) => result.json());
      }
      eventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'heirmuffs-system-builder', sourceId: this.id, itemCount: items.length, sections: payload.sections, didError: false }
      });
      const success = this.querySelector('[data-success]');
      success.hidden = false;
      success.innerHTML = 'Your selections were added as separate cart lines. <a href="' + escapeHTML(this.dataset.cartUrl) + '">Review your cart</a>.';
      this.announce('Your selected products were added to the cart as separate items.');
      button.textContent = 'Added to Cart';
    } catch (error) {
      eventPromise.reject(error);
      this.showError(error.message + ' Your selections are still here so you can review and try again.');
      button.disabled = false;
      button.textContent = original;
    } finally {
      this.busy = false;
    }
  }

  fail(message, control) {
    if (control) {
      control.setAttribute('aria-invalid', 'true');
      control.setAttribute('aria-describedby', 'taj-builder-errors');
      control.focus();
    }
    this.showError(message);
    return false;
  }
  showError(message) {
    this.errors.hidden = false;
    this.errors.textContent = message;
    this.errors.scrollIntoView({ block: 'nearest' });
    this.announce(message);
  }
  clearError() {
    this.errors.hidden = true;
    this.errors.textContent = '';
    this.querySelectorAll('[aria-describedby="taj-builder-errors"]').forEach((control) => {
      control.removeAttribute('aria-invalid');
      control.removeAttribute('aria-describedby');
    });
  }
  announce(message) { this.status.textContent = ''; requestAnimationFrame(() => { this.status.textContent = message; }); }

  save() {
    try {
      const compact = {
        path: this.state.path, step: this.state.step, stemType: this.state.stemType,
        front: this.compactItem(this.state.front), stem: this.compactItem(this.state.stem), backs: this.compactItem(this.state.backs),
        returningFronts: this.state.returningFronts.map((item) => this.compactItem(item))
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(compact));
    } catch (error) {}
  }

  compactItem(item) {
    return item ? { handle: item.handle || item.product?.handle, variantId: item.variant?.id || null } : null;
  }

  restore() {
    try {
      const requestedPath = new URL(window.location.href).searchParams.get('path');
      if (requestedPath === 'returning') {
        this.state.path = 'returning';
        this.state.step = 1;
        this.saved = null;
        return;
      }
      const saved = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
      if (!saved) return;
      this.state.path = saved.path === 'returning' ? 'returning' : 'first';
      this.state.step = Math.max(0, Math.min(4, Number(saved.step) || 0));
      this.state.stemType = saved.stemType || '';
      this.saved = saved;
    } catch (error) {}
  }

  async hydrateSavedSelections() {
    if (!this.saved) return;
    try {
      if (this.saved.front?.handle) {
        const product = await this.fetchProduct('/products/' + encodeURIComponent(this.saved.front.handle) + '.js');
        const variant = product.variants.find((item) => Number(item.id) === Number(this.saved.front.variantId));
        this.state.front = { handle: product.handle, product, variant: variant || null, options: variant?.options || [] };
        this.renderProductSelection('front', product, this.frontSelection, variant);
        this.frontSelection.hidden = false;
      }
      if (this.saved.stem?.handle && this.state.stemType) {
        const url = this.state.stemType === 'adult' ? this.dataset.adultUrl : this.dataset.childUrl;
        const product = await this.fetchProduct(url);
        const variant = product.variants.find((item) => Number(item.id) === Number(this.saved.stem.variantId));
        this.state.stem = { handle: product.handle, product, variant: variant || null, options: variant?.options || [] };
        this.renderProductSelection('stem', product, this.stemSelection, variant);
      }
      if (this.saved.backs?.handle) {
        const product = await this.fetchProduct(this.dataset.backsUrl);
        const variant = product.variants.find((item) => Number(item.id) === Number(this.saved.backs.variantId));
        this.state.backs = { handle: product.handle, product, variant: variant || null, options: variant?.options || [] };
        this.renderProductSelection('backs', product, this.backsSelection, variant);
      }
      this.state.returningFronts = [];
      for (const savedItem of this.saved.returningFronts || []) {
        const product = await this.fetchProduct('/products/' + encodeURIComponent(savedItem.handle) + '.js');
        const variant = product.variants.find((item) => Number(item.id) === Number(savedItem.variantId));
        if (variant) this.state.returningFronts.push({ handle: product.handle, product, variant, options: variant.options });
      }
      this.renderReturningList();
    } catch (error) {
      this.state.step = 0;
      this.showError('Some saved builder choices are no longer available. Please review your selections.');
    }
  }
}

if (!customElements.get('heirmuffs-system-builder')) {
  customElements.define('heirmuffs-system-builder', HeirmuffsSystemBuilder);
}